// YERİ: src/app/domains/website/core/language.store.ts
// Vitrinin dil durumu + '/:lang' önekinin bekçisi.

import { computed, inject, Injectable, signal } from '@angular/core';
import {
  CanActivateFn,
  Router,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { readLanguageSignals, resolvePreferredLanguage } from './language-preference';
import { WebCmsService } from './web-cms.service';
import { WebLanguage } from './web.models';

/** Backend hiç yanıt vermezse bile site çalışsın — vitrin İngilizce yayınlanıyor. */
const FALLBACK: WebLanguage[] = [
  { code: 'en', name: 'English (US)', nativeName: 'English (US)', isRtl: false, isDefault: true },
];

@Injectable({ providedIn: 'root' })
export class LanguageStore {
  private cms = inject(WebCmsService);

  readonly languages = signal<WebLanguage[]>([]);

  /** Aktif rotanın dili. langGuard her gezinmede yazar. */
  readonly current = signal<string>('en');

  /**
   * `current` gerçekten bir vitrin rotasından mı geldi?
   *
   * Auth ekranları dili buradan öğreniyor ve ayrım şart: `current` başlangıçta
   * da 'en' olduğu için, "İngilizce vitrini gezip geldi" ile "doğrudan
   * /auth/sign-in yazdı" ayırt edilemiyordu. İlkinde İngilizce göstermek
   * doğru, ikincisinde ziyaretçinin tarayıcı diline düşmek daha doğru.
   */
  readonly resolvedFromRoute = signal(false);

  readonly defaultCode = computed(
    () => (this.languages().find((l) => l.isDefault) ?? this.languages()[0])?.code ?? 'en',
  );

  /** Dil seçicide gösterilecekler — tek dil varsa seçici anlamsız, gizlenir. */
  readonly showSwitcher = computed(() => this.languages().length > 1);

  /** Aktif dilin kaydı (bulunamazsa undefined — henüz yüklenmemiş olabilir). */
  readonly currentLanguage = computed(() =>
    this.languages().find((l) => l.code === this.current()),
  );

  /**
   * Sayfa yönü. Arapça/Farsça'da yalnız metin değil TÜM düzen döner: kenar
   * çubuğu, ok yönleri, boşluklar. Tek bir dir="rtl" ile tarayıcı bunu kendi
   * yapar; her bileşeni ayrıca çevirmek gerekmez.
   */
  readonly dir = computed<'ltr' | 'rtl'>(() =>
    this.currentLanguage()?.isRtl ? 'rtl' : 'ltr',
  );

  /**
   * Diller yalnız BİR KEZ çekilir. Guard her gezinmede çağırır; shareReplay
   * sayesinde ikinci istek gitmez. Aynı akış SSR'da da çalışır — transfer cache
   * yanıtı HTML'e gömer, istemci tekrar sormaz.
   */
  private languages$?: Observable<WebLanguage[]>;

  load(): Observable<WebLanguage[]> {
    this.languages$ ??= this.cms.getLanguages().pipe(
      map((list) => (list.length > 0 ? list : FALLBACK)),
      // Dil listesi çekilemezse SİTEYİ ÇÖKERTME — varsayılana düş.
      catchError(() => of(FALLBACK)),
      tap((list) => this.languages.set(list)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.languages$;
  }

  isActive(code: string): boolean {
    return this.languages().some((l) => l.code === code);
  }

  /** Verilen dil sağdan sola mı — sayfa yönünü içeriğin diline göre ayarlarken. */
  isRtl(code: string): boolean {
    return this.languages().find((l) => l.code === code)?.isRtl ?? false;
  }
}

/**
 * '/:lang' önekini doğrular.
 *
 * Bu bekçi routing'in emniyet supabıdır: website rotası app.routes.ts'te EN SONDA
 * durur (yoksa ':lang' /admin'i yutardı), ve buraya düşen her şey bir dil koduyla
 * başlamak zorundadır.
 *
 * Başlamıyorsa — '/' ya da '/pricing' gibi — ziyaretçiye UYGUN dile
 * yönlendiririz: açık seçimi (çerez) varsa o, yoksa tarayıcı dili, o da
 * tutmuyorsa varsayılan. Bkz. language-preference.ts.
 *
 * Önekli adreslere DOKUNULMAZ: '/en/pricing' isteyen ziyaretçi orada kalır,
 * çerezi Farsça olsa bile. URL açık bir niyettir ve paylaşılan bağlantının
 * herkeste aynı sayfayı açması gerekir.
 *
 * Sinyaller SUNUCUDA istek başlıklarından okunur (REQUEST), yani yönlendirme
 * ilk render'da olur — istemcide ikinci bir sıçrama yaşanmaz.
 */
export const langGuard: CanActivateFn = (route, state) => {
  const store = inject(LanguageStore);
  const router = inject(Router);
  const signals = readLanguageSignals();

  return store.load().pipe(
    map((languages): boolean | UrlTree => {
      const requested = route.paramMap.get('lang') ?? '';

      if (languages.some((l) => l.code === requested)) {
        store.current.set(requested);
        store.resolvedFromRoute.set(true);
        return true;
      }

      // ':lang' bir dil değil → aslında yolun ilk parçasıydı. Onu koru,
      // başına ziyaretçinin dilini ekle: /pricing → /fa/pricing
      const fallback = (languages.find((l) => l.isDefault) ?? languages[0]).code;
      const preferred = resolvePreferredLanguage(signals, languages, fallback);

      store.current.set(preferred);
      store.resolvedFromRoute.set(true);

      const rest = segmentsAfterLang(state.url);
      // Query and fragment survive the redirect: campaign links carry
      // utm_* parameters, and dropping them lost the traffic source for our own
      // analytics and GA4 alike (/?utm_source=x → /en).
      const parsed = router.parseUrl(state.url);
      return router.createUrlTree([preferred, ...rest], {
        queryParams: parsed.queryParams,
        fragment: parsed.fragment ?? undefined,
      });
    }),
  );
};

/**
 * URL'in ':lang' konumundaki parçası dahil, ondan sonraki tüm parçalar.
 * (Yanlış dil kodu aslında yolun kendisiydi — atılmaz, korunur.)
 */
function segmentsAfterLang(url: string): string[] {
  const path = url.split('?')[0].split('#')[0];
  return path.split('/').filter(Boolean);
}

/** UrlSegment[] → düz string dizisi (catch-all rotalarda slug'ı toplamak için). */
export function joinSegments(segments: UrlSegment[]): string {
  return segments.map((s) => s.path).join('/');
}
